import { X, MessageCircle, Bell, Truck, ChevronDown } from "lucide-react";
import { useState } from "react";
import { Language, dictionary } from "../../../utils/dictionary";
import {
  useFacebookSDK,
  EmbeddedSignupResult,
} from "../../../hooks/useFacebookSDK";
import { INTEGRATIONS_UI_CONFIG } from "../config/integrations.config";

const WHATSAPP_LOGO = INTEGRATIONS_UI_CONFIG.whatsapp.logoSrc;
const FLUXI_LOGO = "https://fluxi.s3.amazonaws.com/prod/assets/fluxi/fluxi-logo.svg";

interface ModalWhatsAppIntegrationProps {
  setModalOpen: (open: boolean) => void;
  handleOAuthComplete: (data: EmbeddedSignupResult) => void;
  loading: boolean;
  language: Language;
}

const ModalWhatsAppIntegration = ({
  setModalOpen,
  handleOAuthComplete,
  loading,
  language,
}: ModalWhatsAppIntegrationProps) => {
  const { sdkReady, launchEmbeddedSignup } = useFacebookSDK();
  const [sdkLoading, setSdkLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConnect = async () => {
    setError(null);
    setSdkLoading(true);
    try {
      const result = await launchEmbeddedSignup();
      handleOAuthComplete(result);
    } catch (err: any) {
      console.error("Embedded signup failed:", err);
      setError(err?.message || "Error connecting WhatsApp");
    } finally {
      setSdkLoading(false);
    }
  };

  const isLoading = loading || sdkLoading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[850px] overflow-hidden flex flex-col md:flex-row relative">
        <button
          onClick={() => setModalOpen(false)}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 md:hidden"
        >
          <X size={24} />
        </button>

        {/* Left Side - Content */}
        <div className="flex-1 p-6 md:p-8 flex flex-col">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-12 h-12 bg-[#25D366] rounded-full flex items-center justify-center shrink-0">
              <img src={WHATSAPP_LOGO} alt="WhatsApp" className="w-7 h-7" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 leading-tight">
              {dictionary["whatsapp-connect-title"][language]}
            </h2>
          </div>

          <p className="text-gray-600 mb-6 text-sm leading-relaxed">
            {dictionary["whatsapp-connect-description"][language]}
          </p>

          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3">
              <Bell className="w-5 h-5 text-[#25D366] mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["whatsapp-feature-confirmations"]?.[language] || "Confirmaciones automáticas de pedidos por WhatsApp"}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <MessageCircle className="w-5 h-5 text-[#25D366] mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["whatsapp-feature-messages"]?.[language] || "Mensajes personalizados a tus clientes"}
              </span>
            </div>
            <div className="flex items-start gap-3">
              <Truck className="w-5 h-5 text-[#25D366] mt-0.5 shrink-0" />
              <span className="text-sm text-gray-700">
                {dictionary["whatsapp-feature-tracking"]?.[language] || "Notificaciones de seguimiento de envíos en tiempo real"}
              </span>
            </div>
          </div>

          {error && (
            <div className="w-full mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="mt-auto pt-4 border-t border-gray-100">
            <div className="flex gap-3">
              <button
                className="flex-1 bg-[#25D366] hover:bg-[#1da851] text-white font-semibold py-2.5 px-4 rounded-lg transition-colors text-sm shadow-sm flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleConnect}
                disabled={!sdkReady || isLoading}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <img src={WHATSAPP_LOGO} alt="" className="w-5 h-5" />
                    {dictionary["whatsapp-connect-button"][language]}
                  </>
                )}
              </button>
              <button
                className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-medium py-2.5 px-4 rounded-lg border border-gray-200 transition-colors text-sm"
                onClick={() => setModalOpen(false)}
              >
                {dictionary["cancel"][language]}
              </button>
            </div>
          </div>
        </div>

        {/* Right Side - Visual */}
        <div className="hidden md:flex w-[200px] bg-gradient-to-b from-[#e8faf0] to-white relative items-center justify-center py-6 px-4 border-l border-gray-100 shrink-0">
          <button
            onClick={() => setModalOpen(false)}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 z-10 transition-colors"
          >
            <X size={18} />
          </button>

          <div className="flex flex-col items-center justify-center gap-8 w-full h-full">
            <div className="bg-white rounded-[24px] shadow-[0_4px_20px_rgba(67,104,229,0.15)] border border-blue-100 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
              <img src={FLUXI_LOGO} alt="Fluxi" className="w-full h-auto object-contain" />
            </div>

            <div className="flex flex-col items-center gap-1 text-[#25D366]">
              <ChevronDown strokeWidth={3} size={24} className="animate-[whatsappFade_1.5s_infinite_0ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[whatsappFade_1.5s_infinite_200ms]" />
              <ChevronDown strokeWidth={3} size={24} className="animate-[whatsappFade_1.5s_infinite_400ms]" />
            </div>

            <div className="bg-white rounded-[24px] shadow-sm border border-gray-200 p-5 w-24 h-24 flex items-center justify-center z-10 relative">
              <img src={WHATSAPP_LOGO} alt="WhatsApp" className="w-12 h-12 object-contain" />
            </div>

            <style>{`
              @keyframes whatsappFade {
                0%, 100% { opacity: 0.3; transform: translateY(0); }
                50% { opacity: 1; transform: translateY(3px); }
              }
            `}</style>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalWhatsAppIntegration;
